import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';

import type { AiModelOptionModel, AiModelsCatalogModel } from '@/domain/models/ai-model.js';
import { AiModelOptionModel as AiModelOptionModelClass } from '@/domain/models/ai-model.js';
import { useApp } from '@/presentation/hooks/use-app.js';

type Props = {
    value: string;
    onChange: (modelId: string) => void;
    catalog: AiModelsCatalogModel | null;
    isLoading?: boolean;
    label: string;
    size?: 'small' | 'medium';
    disabled?: boolean;
};

function formatPrice(value: number | null): string | null {
    if (value === null) {
        return null;
    }
    if (value < 0.5) {
        return `$${value.toFixed(2)}/M`;
    }
    return `$${value.toFixed(2)}/M`;
}

function buildOptionLabel(model: AiModelOptionModel, translate: (key: string) => string): string {
    const reason = model.recommendedReason ? translate(`aiModels.reasons.${model.recommendedReason}`) : null;
    const price = formatPrice(model.promptPricePerMillion);
    const suffix = [reason, price].filter(Boolean).join(' · ');
    return suffix ? `${model.name} (${suffix})` : model.name;
}

export function AiModelSelect({ value, onChange, catalog, isLoading = false, label, size = 'small', disabled = false }: Props) {
    const { translate } = useApp();

    const options = useMemo(() => catalog?.models ?? [], [catalog]);

    const selectedOption = useMemo(() => {
        const match = options.find((option) => option.id === value);
        if (match) {
            return match;
        }
        if (!value) {
            return null;
        }
        return AiModelOptionModelClass.with({
            id: value,
            name: value,
            description: null,
            contextLength: null,
            promptPricePerMillion: null,
            completionPricePerMillion: null,
            supportsStructuredOutputs: true,
            recommended: false,
            recommendedReason: null
        });
    }, [options, value]);

    return (
        <Autocomplete
            options={options}
            value={selectedOption}
            onChange={(_, option) => onChange(option?.id ?? '')}
            loading={isLoading}
            disabled={disabled}
            size={size}
            groupBy={(option) => (option.recommended ? translate('aiModels.recommendedGroup') : translate('aiModels.allModelsGroup'))}
            getOptionLabel={(option) => buildOptionLabel(option, translate)}
            isOptionEqualToValue={(left, right) => left.id === right.id}
            noOptionsText={isLoading ? translate('aiModels.loading') : translate('aiModels.empty')}
            renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                    <Box>
                        <Typography variant="body2" fontWeight={option.recommended ? 600 : 400}>
                            {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                            {option.id}
                            {option.recommendedReason ? ` · ${translate(`aiModels.reasons.${option.recommendedReason}`)}` : ''}
                            {option.contextLength ? ` · ${translate('aiModels.context', { n: Math.round(option.contextLength / 1000) })}` : ''}
                            {formatPrice(option.promptPricePerMillion) ? ` · ${formatPrice(option.promptPricePerMillion)}/M in` : ''}
                        </Typography>
                        {option.description && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                {option.description}
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={translate('aiModels.searchPlaceholder')}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        )
                    }}
                />
            )}
        />
    );
}

export function resolveAiModelLabel(catalog: AiModelsCatalogModel | null, modelId: string | null | undefined): string {
    if (!modelId) {
        return '—';
    }
    return catalog?.resolveLabel(modelId) ?? modelId;
}
